import { useEffect } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  titleId: string
  descriptionId: string
  /** Absolute path of the page, used for the canonical and og:url tags. */
  path?: string
}

const SITE_URL = 'https://sondely.com'

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Title, description and social card for a public page, in the active locale.
 * The SPA has no server render, so the static tags in index.html carry the
 * French default for crawlers and this keeps them in step once React is up.
 */
const DocumentHead: React.FC<Props> = ({ titleId, descriptionId, path = '/' }) => {
  const intl = useIntl()
  const title = intl.formatMessage({ id: titleId })
  const description = intl.formatMessage({ id: descriptionId })
  const url = `${SITE_URL}${path}`

  useEffect(() => {
    document.title = title
    setMeta('meta[name="description"]', 'name', 'description', description)
    setMeta('meta[property="og:title"]', 'property', 'og:title', title)
    setMeta('meta[property="og:description"]', 'property', 'og:description', description)
    setMeta('meta[property="og:url"]', 'property', 'og:url', url)
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
    setMeta('meta[property="og:image"]', 'property', 'og:image', `${SITE_URL}/og-image.png`)
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', intl.locale)
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', `${SITE_URL}/og-image.png`)
    setLink('canonical', url)
  }, [title, description, url, intl.locale])

  return null
}

export default DocumentHead
